import { useMFExecutionContext } from '../../../state-management/contexts/MFExecutionContext'
import BackButton from '../../../components/ui-components/BackButton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FolioDetails } from '../../../components/execution/withdraw/FolioDetails'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { useWithdrawContext } from '../../../state-management/contexts/WithdrawContext'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { UnavailableForWithdrawalBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/withdraw/UnavailableForWithdrawalBottomSheet'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { goBack } from '../../../utils/appBridge'
import {
  isInsufficientBalanceFolio,
  sortFoliosForWithdrawalSelection,
  sortUnavailableFoliosForWithdrawalSelection
} from '../../../utils/mf-execution/withdrawUtils'
import '../../../components/skeletons/skeleton.css'

export const FolioSelectionPage = () => {
  const { mfExecutionData, isMFExecutionDataLoading } = useMFExecutionContext()
  const { handleFolioSelection } = useWithdrawContext()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const folioDetails = mfExecutionData?.sell?.folioDetails ?? []

  const availableFolios = sortFoliosForWithdrawalSelection(
    folioDetails.filter((folio) => !folio?.isUnavailable && !isInsufficientBalanceFolio(folio))
  )
  const unavailableFolios = sortUnavailableFoliosForWithdrawalSelection(
    folioDetails.filter((folio) => folio?.isUnavailable || isInsufficientBalanceFolio(folio))
  )

  if (isMFExecutionDataLoading) {
    return (
      <FolioSelectionPageSkeleton
        onBack={() => {
          if (searchParams?.get('from') === 'APP') goBack()
          else navigate(-1)
        }}
      />
    )
  }

  if (folioDetails.length === 0) {
    return <FolioSelectionEmptyPage />
  }

  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      test-locator='SELECT_FOLIO_BROKER'
      className='w-full h-screen relative overflow-hidden'
    >
      <div className='flex flex-col h-full'>
        <div className='px-5 pb-2'>
          <BackButton
            size={24}
            onClick={() => {
              if (searchParams?.get('from') === 'APP') goBack()
              else navigate(-1)
            }}
          />
        </div>
        <div className='flex flex-1 h-1 overflow-y-auto w-full'>
          <div className='flex flex-col pt-4 w-full'>
            <div className='text-[20px] font-[500] text-[#232323] leading-[130%] px-5'>
              Select a folio for withdrawal
            </div>
            <div className='text-[13px] font-[400] text-[#6A6E6E] leading-[150%] px-5 pt-1 pl-[22px]'>
              {roundOffAndFormatNumberWithCurrency(mfExecutionData?.sell?.totalAvailableAmount)} total available
            </div>
            {availableFolios?.length > 0 && (
              <div className='flex flex-col gap-[18px] pt-5 w-full'>
                {availableFolios.map((folio) => (
                  <div key={folio?.folioNumber} onClick={() => handleFolioSelection(folio)}>
                    <FolioDetails folioDetails={folio} />
                  </div>
                ))}
              </div>
            )}
            {unavailableFolios?.length > 0 && (
              <>
                <div className='pt-5 pb-6 pl-[1px]'>
                  <div className={`w-full ${availableFolios?.length > 0 ? 'h-3' : 'h-[1px]'} bg-[#F0F4F7]`}></div>
                </div>
                <div className={`text-[13px] font-[400] text-[#6A6E6E] leading-[150%] px-5 pl-[22px] flex items-center gap-[6px]`}>
                  <div> Unavailable for withdrawal </div>{' '}
                  <div
                    onClick={() =>
                      openBottomSheet(
                        <UnavailableForWithdrawalBottomSheet
                          onDismiss={closeBottomSheet}
                          ScreenName='FolioSelectionPage'
                        />
                      )
                    }
                    className='mt-[2px]'
                  >
                    <RenderHTML innerHTMLText={InfoIcon} />{' '}
                  </div>
                </div>
                <div className='flex flex-col gap-[18px] pt-1 opacity-80 w-full'>
                  {unavailableFolios?.map((folio, index) => (
                    <div className='grayscale-[1]' key={folio?.folioNumber}>
                      <FolioDetails isBorderTop={index !== 0} folioDetails={folio} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function FolioSelectionPageSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      className='w-full h-screen relative overflow-hidden bg-white'
    >
      <div className='flex flex-col h-full'>
        <div className='px-5 pb-2'>
          <BackButton size={24} onClick={onBack} />
        </div>
        <div className='flex flex-1 h-1 overflow-y-auto no-scrollbar w-full'>
          <div className='flex flex-col pt-4 w-full'>
            <div className='text-[20px] font-[500] text-[#232323] leading-[130%] px-5'>
              Select a folio for withdrawal
            </div>
            <div className='px-5 pt-1 pl-[22px]'>
              <div className='skeleton h-[20px] w-[148px] rounded-[4px]' />
            </div>
            <div className='flex flex-col gap-[18px] pt-5 w-full'>
              {[0, 1, 2].map((folioIndex) => (
                <FolioSelectionRowSkeleton key={folioIndex} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function FolioSelectionRowSkeleton() {
  return (
    <div className='flex flex-col'>
      <div className='border-t border-[#e2e8f0] w-full' />
      <div className='flex items-center justify-between pt-4 px-5'>
        <div className='flex gap-3 flex-1'>
          <div className='skeleton h-[40px] w-[40px] shrink-0 rounded-[12px]' />
          <div className='flex flex-col gap-[2px] flex-1'>
            <div className='skeleton h-[18px] w-[168px] rounded-[4px]' />
            <div className='skeleton h-[20px] w-[112px] rounded-[4px]' />
            <div className='flex items-center pt-2'>
              <div className='skeleton h-[24px] w-[140px] rounded-[8px]' />
            </div>
          </div>
        </div>
        <div className='skeleton h-[14px] w-5 shrink-0 rounded-[2px]' />
      </div>
    </div>
  )
}

export function FolioSelectionEmptyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      className='w-full h-screen relative overflow-hidden'
    >
      <div className='flex flex-col h-full'>
        <div className='px-5 pb-2'>
          <BackButton
            size={24}
            onClick={() => {
              if (searchParams?.get('from') === 'APP') goBack()
              else navigate(-1)
            }}
          />
        </div>
        <div className='flex flex-1 flex-col items-center justify-center px-5 pb-16'>
          <RenderHTML innerHTMLText={NoFolioFoundIcon} />
          <div className='mt-4 text-center text-[20px] font-[500] leading-[130%] text-[#232323]'>
            No folios found
          </div>
          <div className='mt-1 text-center text-[14px] font-[400] leading-[150%] text-[#6A6E6E]'>
            There are no folios available for withdrawal in this fund.
          </div>
        </div>
      </div>
    </div>
  )
}

const NoFolioFoundIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <rect x="10" y="8" width="28" height="32" rx="4" stroke="#8E9393" stroke-width="1.8"/>
  <path d="M18 20h12M18 26h8" stroke="#8E9393" stroke-width="1.8" stroke-linecap="round"/>
</svg>`

const InfoIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g opacity="0.5" clip-path="url(#clip0_36453_269318)">
      <path d="M6 0C4.81332 0 3.65328 0.351894 2.66658 1.01118C1.67989 1.67047 0.910851 2.60754 0.456726 3.7039C0.0025997 4.80026 -0.11622 6.00666 0.115291 7.17054C0.346802 8.33443 0.918247 9.40353 1.75736 10.2426C2.59648 11.0818 3.66558 11.6532 4.82946 11.8847C5.99335 12.1162 7.19975 11.9974 8.2961 11.5433C9.39246 11.0892 10.3295 10.3201 10.9888 9.33342C11.6481 8.34673 12 7.18669 12 6C11.9981 4.40929 11.3653 2.88427 10.2405 1.75946C9.11573 0.634658 7.59072 0.00190543 6 0V0ZM6.75 9C6.75 9.19891 6.67099 9.38968 6.53033 9.53033C6.38968 9.67098 6.19892 9.75 6 9.75C5.80109 9.75 5.61033 9.67098 5.46967 9.53033C5.32902 9.38968 5.25 9.19891 5.25 9V6.6C5.25 6.40109 5.32902 6.21032 5.46967 6.06967C5.61033 5.92902 5.80109 5.85 6 5.85C6.19892 5.85 6.38968 5.92902 6.53033 6.06967C6.67099 6.21032 6.75 6.40109 6.75 6.6V9ZM6 4.8C5.76267 4.8 5.53066 4.72962 5.33332 4.59776C5.13598 4.46591 4.98217 4.27849 4.89135 4.05922C4.80052 3.83995 4.77676 3.59867 4.82306 3.36589C4.86936 3.13311 4.98365 2.91929 5.15147 2.75147C5.3193 2.58365 5.53312 2.46936 5.76589 2.42306C5.99867 2.37676 6.23995 2.40052 6.45922 2.49134C6.67849 2.58217 6.86591 2.73598 6.99777 2.93332C7.12962 3.13066 7.2 3.36266 7.2 3.6C7.2 3.91826 7.07357 4.22348 6.84853 4.44853C6.62349 4.67357 6.31826 4.8 6 4.8Z" fill="#8E9393"/>
  </g>
  <defs>
    <clipPath id="clip0_36453_269318">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`
